import { Origin, Horoscope } from 'circular-natal-horoscope-js';

/**
 * Calculate birth chart using CircularNatalHoroscopeJS
 * @param {Date} birthDate - Birth date and time (UTC)
 * @param {number} latitude - Birth latitude
 * @param {number} longitude - Birth longitude
 * @param {boolean} showModernPlanets - Whether to include Uranus and Neptune
 * @param {string} houseSystem - House system to use (default: 'whole-sign')
 * @returns {Promise<Object>} - Birth chart data compatible with AstroChart
 */
export async function calculateBirthChartWithLibrary(year, month, day, hour, minute, second, latitude, longitude, showModernPlanets = false, houseSystem = 'whole-sign') {
  try {
    // Validate house system parameter
    const validHouseSystems = ['whole-sign', 'placidus', 'koch', 'equal-house', 'campanus', 'regiomontanus', 'topocentric'];
    if (!validHouseSystems.includes(houseSystem)) {
      console.warn(`Invalid house system "${houseSystem}", falling back to "whole-sign"`);
      houseSystem = 'whole-sign';
    }

    // FIXED: Now we're receiving local time components directly
    // CircularNatalHoroscopeJS expects local time components
    
    console.log('🔍 HOUSE SYSTEM DEBUG:');
    console.log('🔍 Selected house system:', houseSystem);
    console.log('🔍 Valid house systems:', validHouseSystems);
    console.log('🔍 Local time components:', { year, month, day, hour, minute, second });
    console.log('🔍 Latitude:', latitude, 'Longitude:', longitude);

    // Use the time components directly (month is already 0-indexed from Luxon)
    // Note: CircularNatalHoroscopeJS expects month to be 0-indexed

    console.log('🔍 CIRCULAR NATAL HOROSCOPE - Input data:');
    console.log('🔍 Year:', year, 'Month:', month, 'Day:', day);
    console.log('🔍 Hour:', hour, 'Minute:', minute, 'Second:', second);
    console.log('🔍 Latitude:', latitude, 'Longitude:', longitude);
    console.log('🔍 House System:', houseSystem);

    // Create Origin object for CircularNatalHoroscopeJS with local time
    const origin = new Origin({
      year,
      month,
      date: day, // Use 'day' parameter
      hour,
      minute,
      second,
      latitude,
      longitude
    });

    console.log('🔍 CIRCULAR NATAL HOROSCOPE - Origin created:');
    console.log('🔍 UTC Time:', origin.utcTime.format());
    console.log('🔍 Local Time:', origin.localTime.format());
    console.log('🔍 Timezone:', origin.timezone.name);
    console.log('🔍 Julian Date:', origin.julianDate);
    console.log('🔍 Local Sidereal Time:', origin.localSiderealTime);

    // Create Horoscope object with the selected house system
    const horoscope = new Horoscope({
      origin: origin,
      houseSystem: houseSystem, // Use the passed house system
      zodiac: 'tropical',
      aspectPoints: ['bodies', 'points', 'angles'],
      aspectWithPoints: ['bodies', 'points', 'angles'],
      aspectTypes: ['major', 'minor'],
      customOrbs: {},
      language: 'en'
    });

    console.log('🔍 CIRCULAR NATAL HOROSCOPE - Horoscope created:');
    console.log('🔍 Ascendant:', horoscope.Ascendant.ChartPosition.Ecliptic.DecimalDegrees.toFixed(2), '°');
    console.log('🔍 Ascendant Sign:', horoscope.Ascendant.Sign.label);
    console.log('🔍 House System Used:', houseSystem);

    // Extract planets data - format for ChartDrawer
    const planets = {};
    Object.keys(horoscope.CelestialBodies).forEach(key => {
      if (key !== 'all') {
        const body = horoscope.CelestialBodies[key];
        // Convert planet names to match ChartDrawer expectations
        const planetName = key.charAt(0).toUpperCase() + key.slice(1);
        planets[planetName] = {
          longitude: body.ChartPosition.Ecliptic.DecimalDegrees
        };
      }
    });

    // Extract house cusps - use StartPosition for house cusps
    const houses = horoscope.Houses.map((house, index) => ({
      house: index + 1,
      angle: house.ChartPosition.StartPosition.Ecliptic.DecimalDegrees,
      sign: house.Sign.label
    }));

    // Extract cusps for AstroChart
    const cusps = houses.map(house => house.angle);

    console.log('🔍 CIRCULAR NATAL HOROSCOPE - Final data:');
    console.log('🔍 Planets:', Object.keys(planets));
    console.log('🔍 Houses:', houses.length);
    console.log('🔍 Cusps:', cusps.length);

    // Return data in the same format as your existing system
    return {
      birthDate: new Date(year, month, day, hour, minute, second).toISOString(),
      latitude,
      longitude,
      julianDay: origin.julianDate,
      planets,
      houses,
      cusps,
      showModernPlanets,
      ascendantLongitude: horoscope.Ascendant.ChartPosition.Ecliptic.DecimalDegrees
    };

  } catch (error) {
    console.error('❌ CIRCULAR NATAL HOROSCOPE - Error:', error);
    throw new Error(`Failed to calculate birth chart: ${error.message}`);
  }
}

/**
 * Get planet image path (keeping your existing function)
 */
function getPlanetImage(planetName) {
  const planetImages = {
    'Sun': '/img/planets/sun.png',
    'Moon': '/img/planets/moon.png',
    'Mercury': '/img/planets/mercury.png',
    'Venus': '/img/planets/venus.png',
    'Mars': '/img/planets/mars.png',
    'Jupiter': '/img/planets/jupiter.png',
    'Saturn': '/img/planets/saturn.png',
    'Uranus': '/img/planets/uranus.png',
    'Neptune': '/img/planets/neptune.png',
    'Pluto': '/img/planets/pluto.png'
  };
  
  return planetImages[planetName] || '/img/planets/sun.png';
} 